import type { WorkItem } from '../workitems/workItemTypes';
import { deviceReleaseSummary } from '../../utils/deviceRelease';
import {
  normalizeStatus,
  formatStatusDisplay,
  formatCustomerDisplay,
  formatCustomerShort,
  getBillingSummary,
  formatSmartDate,
} from '../workitems/utils/workItemUtils';
import { hydrateChecklist, computeChecklistProgress } from '../../utils/checklistEngine';
import type { JobStatusKey } from './JobRow';
import { toStatusDropdownValue } from './statusPickerOptions';
import {
  lifecycleLabel,
  lifecycleChipColor,
  intakeTypeLabel,
  formatAud,
  type ChipColor,
} from '../check-in/sessionDetail/sessionDetailHelpers';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Maps normalized status slugs to the JobRow colour keys (the 8-colour map). */
const STATUS_KEY: Record<string, JobStatusKey> = {
  'new': 'new',
  'ready-for-diagnostics': 'diagnostics',
  'in-progress': 'in-progress',
  'resolved': 'resolved',
  'closed': 'resolved',
  'waiting-for-parts': 'parts',
  'parts-to-be-ordered': 'parts',
  'waiting-on-customer': 'customer',
  'customer-reply': 'customer',
  'with-3rd-party-for-repairs': 'third-party',
  'waiting-on-supplier-for-quote': 'supplier',
  // Set by check-in Defer (New customer): diagnostics wait on the invoice.
  'waiting-on-payment': 'payment',
};

/** Resolve a work item's status to a JobRow colour key, defaulting to "new". */
export function jobStatusKey(status?: string): JobStatusKey {
  return STATUS_KEY[normalizeStatus(status ?? '')] ?? 'new';
}

export function isAppointmentItem(w: WorkItem): boolean {
  return w.type === 'appointment' || w.type === 'job-appointment';
}

export function isCheckInItem(w: WorkItem): boolean {
  return w.type === 'check-in';
}

/** Two-letter initials from a full name, e.g. "Steve Thomas" → "ST". */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** "09 Jun 26" */
export function formatRowDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
}

/** "14:15" (24h, en-AU) */
export function formatRowTime(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export interface JobRowData {
  ticket: string;
  ticketHref?: string;
  date: string;
  customer: string;
  /** Full "Business - Full Name" form, surfaced as the customer hover text. */
  customerTitle: string;
  /** True when the customer is a business — shows a building icon by the name. */
  isBusiness: boolean;
  title: string;
  /** Secondary line under the title — the most recent job-log/activity text. */
  subtitle?: string;
  /** Most recent phone call, shown under the customer name. */
  lastCall?: { direction: 'inbound' | 'outbound'; time: string };
  /** Task-list progress chip, e.g. "Tasks (2 of 5)". */
  taskLabel?: string;
  /** True when all tasks are closed (drives the chip colour). */
  taskComplete?: boolean;
  /** Set while the device is out with the customer awaiting parts — the
   *  chip's hover text ("Battery, ETA 2 weeks · since 20 Oct"). */
  deviceWithCustomer?: string;
  status: JobStatusKey;
  statusLabel: string;
  /** Current status in the StatusDropdown value space (for the active highlight). */
  statusValue: string;
  priceLines: PriceLine[];
}

export interface PriceLine {
  amount: string;
  label: string;
  paid: boolean;
}

/** "Paid" → true; "Invoiced, unpaid" / "Not invoiced" → false. */
const isPaidLabel = (label: string): boolean =>
  label.toLowerCase().includes('paid') && !label.toLowerCase().includes('unpaid');

export function toJobRowProps(w: WorkItem): JobRowData {
  const lastCallRaw = w.phoneCalls?.[0];

  let taskLabel: string | undefined;
  let taskComplete: boolean | undefined;
  if (w.taskList) {
    const p = computeChecklistProgress(hydrateChecklist(w.taskList));
    if (p.total > 0) {
      taskLabel = `Tasks (${p.closed} of ${p.total})`;
      taskComplete = p.closed >= p.total;
    }
  }

  return {
    ticket: String(w.number || w.id),
    ticketHref: w.number ? `https://techsolvers.repairshopr.com/tickets/${w.id}` : undefined,
    date: formatRowDate(w.createdOn),
    customer: formatCustomerShort(w),
    customerTitle: formatCustomerDisplay(w),
    isBusiness: !!w.customerBusinessName,
    title: w.title ?? '',
    subtitle: w.lastJobLog?.text || undefined,
    lastCall: lastCallRaw
      ? { direction: lastCallRaw.type, time: formatSmartDate(lastCallRaw.createdAt) }
      : undefined,
    taskLabel,
    taskComplete,
    deviceWithCustomer: w.deviceWithCustomer ? deviceReleaseSummary(w) : undefined,
    status: jobStatusKey(w.status),
    statusLabel: formatStatusDisplay(w.status ?? 'New'),
    statusValue: toStatusDropdownValue(w.status),
    priceLines: toPriceLines(w),
  };
}

function toPriceLines(w: WorkItem): PriceLine[] {
  const { segments } = getBillingSummary(w);
  if (segments.length === 0) {
    return [{ amount: 'No line items', label: '', paid: false }];
  }
  return segments.map((s) => ({ amount: s.amount, label: s.label, paid: isPaidLabel(s.label) }));
}

export interface AppointmentRowData {
  time: string;
  date: string;
  customer: string;
  /** Full "Business - Full Name" form, surfaced as the customer hover text. */
  customerTitle: string;
  title: string;
  statusLabel: string;
  /** Normalised current status (matches a StatusOption.value) for the change menu. */
  status: string;
  priceText: string;
  appointmentDate: Date;
}

export function toAppointmentRowProps(w: WorkItem): AppointmentRowData {
  return {
    time: formatRowTime(w.startAt),
    date: formatRowDate(w.startAt),
    customer: formatCustomerShort(w),
    customerTitle: formatCustomerDisplay(w),
    title: w.title ?? '',
    statusLabel: formatStatusDisplay(w.status || w.appointmentStatus || 'Scheduled'),
    status: normalizeStatus(w.status || w.appointmentStatus || 'Scheduled'),
    priceText: getBillingSummary(w).label,
    appointmentDate: w.startAt ? new Date(w.startAt) : new Date(0),
  };
}

export interface CheckInRowData {
  /** Session number (e.g. "CI-1023"); falls back to a "Check-in" placeholder. */
  sessionNumber: string;
  date: string;
  customer: string;
  /** Full "Business - Full Name" hover text shown on the customer name. */
  customerTitle: string;
  /** Shows a building icon before the customer name for business customers. */
  isBusiness: boolean;
  title: string;
  /** Secondary line under the title — e.g. "In-Store · 2 devices". */
  subtitle?: string;
  /** Lifecycle status label, e.g. "Awaiting Intake". */
  statusLabel: string;
  /** Lifecycle chip colour — drives the row stripe, dot, and status text. */
  statusColor: ChipColor;
  /** True when the session is flagged as needing attention (red marker). */
  requiresAttention: boolean;
  /** Preliminary charges, pre-formatted as AUD; empty when there are none. */
  chargesText: string;
}

export function toCheckInRowProps(w: WorkItem): CheckInRowData {
  const intake = w.checkInIntakeType ? intakeTypeLabel(w.checkInIntakeType) : '';
  const deviceCount = w.checkInDraftJobs?.length ?? 0;
  const subtitleParts = [
    intake,
    deviceCount > 0 ? `${deviceCount} device${deviceCount === 1 ? '' : 's'}` : '',
  ].filter(Boolean);

  const total = w.checkInPreliminaryChargesTotal;
  const chargesText = typeof total === 'number' && total > 0 ? formatAud(total) : '';

  // Lifecycle status is the meaningful state for a check-in; fall back to the
  // generic work-item status when the projection didn't carry a lifecycle.
  const lifecycle = w.checkInLifecycleStatus || w.status || '';

  return {
    sessionNumber: w.checkInSessionNumber || (w.number ? String(w.number) : 'Check-in'),
    date: formatRowDate(w.createdOn),
    customer: formatCustomerShort(w),
    customerTitle: formatCustomerDisplay(w),
    isBusiness: !!w.customerBusinessName,
    title: w.title ?? '',
    subtitle: subtitleParts.join(' · ') || undefined,
    statusLabel: lifecycleLabel(lifecycle) || 'Check-in',
    statusColor: lifecycleChipColor(lifecycle),
    requiresAttention: !!w.checkInRequiresAttention,
    chargesText,
  };
}
